import { FC, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  erc20Abi,
  Address,
  formatEther,
  EIP1193Provider,
  parseEther,
} from 'viem'
import {
  useAccount,
  useSwitchChain,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
} from 'wagmi'
import {
  ConnectWalletKapButton,
  ConnectWalletKapSolidButton,
  SolidButton,
} from '../../common/components'
import { Header } from '../../common/Header'
import {
  NotificationArg,
  NotificationTypes,
} from '../../common/NotificationCard'
import {
  abstractChainId,
  abstractExplorerUrl,
  cncStakingContractConfig,
  cncTokenAddress,
} from './cncStakingContractConfigs'
import { ethers } from 'ethers'
import StakingCnCURL from '../../assets/StakingCCLogo.png'

const Icons = {
  Staking: () => (
    <svg
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.50158 11.5793L14.4186 19.1628C14.5962 19.2755 14.7988 19.3318 15.0003 19.3318C15.2029 19.3318 15.4044 19.2755 15.5821 19.1628L27.4991 11.5793C27.8111 11.3799 28.0006 11.0354 28.0006 10.6649C28.0006 10.2944 27.8111 9.94991 27.4991 9.75166L15.582 2.16704C15.2267 1.94172 14.7738 1.94172 14.4185 2.16704L2.50158 9.75166C2.18957 9.94991 2 10.2944 2 10.6649C1.99995 11.0354 2.18957 11.3799 2.50158 11.5793Z"
        fill="#E3E3E5"
      />
      <path
        opacity="0.5"
        d="M27.499 18.4185L24.1439 16.2832L16.7456 20.9915C16.2202 21.3241 15.6178 21.4996 15.0003 21.4996C14.3828 21.4996 13.7805 21.3241 13.2583 20.9926L5.85673 16.2832L2.50158 18.4185C2.18957 18.6178 2 18.9623 2 19.3328C2 19.7034 2.18957 20.0479 2.50158 20.2461L14.4186 27.8307C14.5962 27.9434 14.7977 27.9997 15.0003 27.9997C15.2018 27.9997 15.4044 27.9434 15.5821 27.8307L27.499 20.2461C27.811 20.0479 28.0006 19.7034 28.0006 19.3328C28.0006 18.9623 27.811 18.6178 27.499 18.4185Z"
        fill="#E3E3E5"
      />
    </svg>
  ),
  DownArrow: () => (
    <svg
      width="14"
      height="16"
      viewBox="0 0 14 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.62248 15.7443L13.4008 9.9661C13.5657 9.80116 13.6563 9.58132 13.6563 9.34692C13.6563 9.11225 13.5656 8.89255 13.4008 8.7276L12.876 8.20299C12.7113 8.03818 12.4914 7.94738 12.2568 7.94738C12.0224 7.94738 11.7951 8.03818 11.6304 8.20299L8.25207 11.5739L8.25207 0.864383C8.25207 0.381525 7.87405 6.8952e-08 7.39107 7.47116e-08L6.64922 8.3558e-08C6.16623 8.93176e-08 5.7501 0.381525 5.7501 0.864383L5.7501 11.6121L2.35293 8.20312C2.18799 8.03831 1.974 7.94751 1.73947 7.94751C1.50519 7.94751 1.28809 8.03831 1.12328 8.20312L0.600227 8.72773C0.435286 8.89268 0.3454 9.11238 0.3454 9.34705C0.3454 9.58145 0.436456 9.80129 0.601398 9.96623L6.37956 15.7444C6.54502 15.9097 6.7659 16.0007 7.00069 16C7.23627 16.0005 7.45728 15.9097 7.62248 15.7443Z"
        fill="#FBFCFD"
      />
    </svg>
  ),
  UpArrow: () => (
    <svg
      width="14"
      height="16"
      viewBox="0 0 14 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.37752 0.255738L0.599227 6.0339C0.434285 6.19884 0.34375 6.41868 0.34375 6.65308C0.34375 6.88775 0.434416 7.10745 0.599227 7.2724L1.12397 7.79701C1.28865 7.96182 1.50862 8.05262 1.74315 8.05262C1.97756 8.05262 2.20494 7.96182 2.36962 7.79701L5.74793 4.42611L5.74793 15.1356C5.74793 15.6185 6.12595 16 6.60893 16L7.35078 16C7.83377 16 8.2499 15.6185 8.2499 15.1356L8.2499 4.38787L11.6471 7.79688C11.812 7.96169 12.026 8.05249 12.2605 8.05249C12.4948 8.05249 12.7119 7.96169 12.8767 7.79688L13.3998 7.27227C13.5647 7.10732 13.6546 6.88762 13.6546 6.65295C13.6546 6.41855 13.5635 6.19871 13.3986 6.03377L7.62044 0.255607C7.45497 0.0902755 7.2341 -0.000650707 6.99931 6.62751e-07C6.76373 -0.000519079 6.54272 0.0902755 6.37752 0.255738Z"
        fill="#FBFCFD"
      />
    </svg>
  ),
}

const getWalletSigner = async (provider: EIP1193Provider) => {
  const ethersProvider = new ethers.BrowserProvider(provider, 'any')
  const signer = await ethersProvider.getSigner()
  return signer
}

const withdrawalLockPeriod = 7 * 3600 * 24 // 7days in seconds

const formatWithdrawLimit = (seconds: number) => {
  const minutes = Math.floor((withdrawalLockPeriod - seconds) / 60)

  if (minutes < 1) {
    return '~ a min'
  } else if (minutes < 60) {
    return `~${minutes} min`
  } else if (minutes < 1440) {
    const hours = Math.floor(minutes / 60)
    return `~${hours} hours`
  } else {
    const days = Math.floor(minutes / 1440)
    return `~${days} days`
  }
}

export const StakingContent: FC<{
  addNotification(args: NotificationArg): void
}> = ({ addNotification }) => {
  const [tab, setTab] = useState<'deposit' | 'withdraw'>('deposit')
  const [depositAmount, setDepositAmount] = useState(0)
  const [withdrawAmount, setWithdrawAmount] = useState(0)
  const [balance, setBalance] = useState(0)

  const [lastDepositMessage, setLastDepositMessage] = useState('')
  const [isConfirmingAllowance, setIsConfirmingAllowance] = useState(false)

  const navigate = useNavigate()

  const { address, connector, chainId } = useAccount()
  const { switchChainAsync } = useSwitchChain()

  const {
    data: balanceWei,
    //  isLoading: isRewardsLoading,
    refetch: refetchBalance,
  } = useReadContract({
    address: cncTokenAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [address as Address],
    query: {
      enabled: !!address,
    },
    chainId: abstractChainId,
  })

  useEffect(() => {
    if (balanceWei) {
      setBalance(parseInt(formatEther(balanceWei)))
    }
  }, [balanceWei])

  const { data: stakedBalanceData, refetch: refetchStakedBalance } =
    useReadContract({
      ...cncStakingContractConfig,
      chainId: abstractChainId,
      functionName: 'balances',
      args: [address as Address, cncTokenAddress],
      query: {
        enabled: !!address,
      },
    })

  const stakedBalance = stakedBalanceData
    ? parseInt(formatEther(stakedBalanceData).toString())
    : 0

  const { data: lastDepositData, refetch: refetchLastDeposit } =
    useReadContract({
      ...cncStakingContractConfig,
      chainId: abstractChainId,
      functionName: 'lastDepositTime',
      args: [address as Address, cncTokenAddress],
      query: {
        enabled: !!address,
      },
    })

  useEffect(() => {
    if (lastDepositData) {
      const lastDeposit = new Date(parseInt(lastDepositData.toString()) * 1000)
      const now = new Date()
      const diff = (now.getTime() - lastDeposit.getTime()) / 1000

      if (diff < withdrawalLockPeriod) {
        setLastDepositMessage(
          'Withdraw will be available in: ' + formatWithdrawLimit(diff)
        )
      } else {
        setLastDepositMessage('')
      }
    } else {
      setLastDepositMessage('')
    }
  }, [lastDepositData])

  const { data: hash, error, writeContract, isPending } = useWriteContract()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  useEffect(() => {
    if (isConfirming) {
      addNotification({
        message: 'Transaction submitted. Please wait.',
        type: NotificationTypes.info,
      })
    }
  }, [isConfirming])

  useEffect(() => {
    if (error) {
      addNotification({
        message: 'Error sending transaction.',
        type: NotificationTypes.error,
      })
    }
  }, [error])

  useEffect(() => {
    if (isSuccess) {
      refetchData()

      addNotification({
        message: 'success',
        type: NotificationTypes.success,
        link: {
          title: 'Tx Link',
          url: abstractExplorerUrl + 'tx/' + hash,
        },
      })
    }
  }, [isSuccess])

  const checkCNCAllowance = async (
    walletProvider: EIP1193Provider,
    count: number,
    callback: () => void
  ) => {
    setIsConfirmingAllowance(true)
    try {
      const signer = await getWalletSigner(walletProvider)

      const cncContract = new ethers.Contract(cncTokenAddress, erc20Abi, signer)

      const ownerAddress = await signer.getAddress()
      const currentAllowance = await cncContract.allowance(
        ownerAddress,
        cncStakingContractConfig.address
      )
      const totalPriceInWei = parseEther(count.toString())

      // console.log(
      //   'Current Allowance:',
      //   currentAllowance,
      //   ethers.formatUnits(currentAllowance, 18),
      //   totalPriceInWei
      // )

      if (currentAllowance < totalPriceInWei) {
        const approvalTx = await cncContract.approve(
          cncStakingContractConfig.address,
          ethers.MaxUint256
        )

        addNotification({
          message: 'Transaction submitted. Please wait.',
          type: NotificationTypes.info,
        })

        await approvalTx.wait()

        addNotification({
          message: 'Token approval successful.',
          type: NotificationTypes.success,
        })
      }

      callback()
    } catch (error) {
    } finally {
      setIsConfirmingAllowance(false)
    }
  }

  const handleDepositConfirm = async () => {
    if (chainId !== abstractChainId) {
      await switchChainAsync({
        chainId: abstractChainId,
      })
    }

    if (!connector) return

    const provider = await connector?.getProvider()

    checkCNCAllowance(provider as EIP1193Provider, depositAmount, () => {
      writeContract({
        ...cncStakingContractConfig,
        functionName: 'deposit',
        args: [
          cncTokenAddress,
          // BigInt(depositAmount),
          parseEther(depositAmount.toString()),
        ],
      })
    })
  }

  const handleWithdrawConfirm = async () => {
    if (chainId !== abstractChainId) {
      await switchChainAsync({
        chainId: abstractChainId,
      })
    }

    writeContract({
      ...cncStakingContractConfig,
      functionName: 'withdraw',
      args: [
        cncTokenAddress,
        // BigInt(withdrawAmount)
        parseEther(withdrawAmount.toString()),
      ],
    })
  }

  const refetchData = () => {
    refetchBalance()
    refetchStakedBalance()
    refetchLastDeposit()
  }

  return (
    <div className="w-[638px] max-md:w-full h-fit p-6 flex flex-col gap-6 rounded-xl 0border 0border-kap-divider">
      <div className="flex items-center gap-4 text-white font-label text-xl max-sm:text-[18px] font-bold">
        <div>
          <Icons.Staking />
        </div>
        <div className="grow">Staking</div>
        <ConnectWalletKapButton />
      </div>

      <div className="flex gap-4">
        <div
          className={`flex-1 px-[14px] py-4 flex max-sm:flex-col items-center gap-4 rounded-lg bg-[#2F3036] bg-opacity-25 border cursor-pointer ${
            tab === 'deposit'
              ? 'border-[#3F4756] bg-opacity-35'
              : 'border-[#101113]'
          }`}
          onClick={() => setTab('deposit')}
        >
          <div className="w-[34px] h-[34px] rounded-full bg-[#2F3036] bg-opacity-50 flex items-center justify-center">
            <Icons.DownArrow />
          </div>
          <div className="font-label text-white text-sm font-medium">
            Deposit
          </div>
        </div>
        <div
          className={`flex-1 px-[14px] py-4 flex max-sm:flex-col items-center gap-4 rounded-lg bg-[#2F3036] bg-opacity-25 border cursor-pointer ${
            tab === 'withdraw'
              ? 'border-[#3F4756] bg-opacity-35'
              : 'border-[#101113]'
          }`}
          onClick={() => setTab('withdraw')}
        >
          <div className="w-[34px] h-[34px] rounded-full bg-[#2F3036] bg-opacity-50 flex items-center justify-center">
            <Icons.UpArrow />
          </div>
          <div className="font-label text-white text-sm font-medium">
            Withdraw
          </div>
        </div>
      </div>

      {tab === 'deposit' && (
        <>
          <div className="py-5 px-6 flex flex-col gap-4 rounded-2xl border border-[#3F4756]">
            <div className="w-full flex items-center gap-2 justify-between">
              <div>Deposit Amount</div>
            </div>

            <div className="py-3 flex items-center gap-3">
              <div className="size-[46px] min-w-[46px] flex items-center justify-center rounded-lg bg-[#2F3036] bg-opacity-25">
                <img className="size-10" src={StakingCnCURL} alt="" />
              </div>
              <div className="grow flex flex-col">
                <div className="flex justify-between text-white text-2xl font-bold">
                  <div className="flex items-center gap-[6px] cursor-pointer text-2xl">
                    CNC
                  </div>
                  <input
                    style={{
                      width: depositAmount.toString().length + 4 + 'ch',
                      // minWidth: '3ch',
                    }}
                    className="bg-transparent p-0 !outline-none border-0 focus:ring-0  text-2xl text-right [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    type="number"
                    value={depositAmount}
                    onChange={(e) => {
                      setDepositAmount(parseInt(e.target.value))
                    }}
                  />
                </div>
                <div
                  className="flex justify-between text-sm cursor-pointer"
                  onClick={() => setDepositAmount(balance)}
                >
                  <div>Balance: {balance} Max</div>
                  {/* <div>
                        ≈ ${' '}
                        {((balance || 0) * 0)
                            {selectedCoinBalances.price}
                          .toFixed(2)}
                      </div> */}
                </div>
              </div>
            </div>
          </div>

          <div className="py-4 px-6 flex flex-col gap-4 rounded-2xl border border-[#3F4756]">
            <div className="flex max-sm:flex-col gap-3 justify-between">
              Your staked balance:
              <div className="text-white">{stakedBalance} $CNC</div>
            </div>
            {/* <div className="flex max-sm:flex-col gap-3 justify-between">
              Your voting power:
              <div className="text-white">{stakedBalance}</div>
            </div> */}
          </div>

          {!address ? (
            <ConnectWalletKapSolidButton />
          ) : (
            <SolidButton
              onClick={handleDepositConfirm}
              disabled={
                !depositAmount ||
                isPending ||
                isConfirming ||
                balance < depositAmount ||
                isConfirmingAllowance
              }
            >
              {balance < depositAmount ? 'Insufficient balance' : `Confirm `}
              {/* ${
                    !depositAmount
                      ? ''
                      : `+${depositAmount.toLocaleString()} Voting Power`
                  } */}
            </SolidButton>
          )}
        </>
      )}

      {tab === 'withdraw' && (
        <>
          <div className="py-5 px-6 flex flex-col gap-4 rounded-2xl border border-[#3F4756]">
            <div className="w-full flex items-center gap-2 justify-between">
              <div>Withdraw Amount</div>
            </div>

            <div className="py-3 flex items-center gap-3">
              <div className="size-[46px] min-w-[46px] flex items-center justify-center rounded-lg bg-[#2F3036] bg-opacity-25">
                <img className="size-10" src={StakingCnCURL} alt="" />
              </div>
              <div className="grow flex flex-col">
                <div className="flex justify-between text-white text-2xl font-bold">
                  <div className="flex items-center gap-[6px] cursor-pointer text-2xl">
                    CNC
                  </div>
                  <input
                    style={{
                      width: withdrawAmount.toString().length + 4 + 'ch',
                      // minWidth: '3ch',
                    }}
                    className="bg-transparent p-0 !outline-none border-0 focus:ring-0 text-2xl text-right [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => {
                      setWithdrawAmount(parseInt(e.target.value))
                    }}
                  />
                </div>
                <div
                  className="flex justify-between text-sm cursor-pointer"
                  onClick={() => setWithdrawAmount(stakedBalance)}
                >
                  <div>Balance: {stakedBalance} Max</div>
                  {/* <div>
                        ≈ ${' '}
                        {((balance || 0) * 0)
                            {selectedCoinBalances.price}
                          .toFixed(2)}
                      </div> */}
                </div>
              </div>
            </div>
          </div>

          <div className="py-4 px-6 flex flex-col gap-4 rounded-2xl border border-[#3F4756]">
            <div className="flex max-sm:flex-col gap-3 justify-between">
              Your staked balance:
              <div className="text-white">{stakedBalance} $CNC</div>
            </div>
            {/* <div className="flex max-sm:flex-col gap-3 justify-between">
              Your voting power:
              <div className="text-white">{stakedBalance}</div>
            </div> */}
          </div>

          {lastDepositMessage && (
            <div className="text-sm italic -my-4">
              Withdraw is locked 7 days after every deposit
            </div>
          )}

          {!address ? (
            <ConnectWalletKapSolidButton />
          ) : (
            <SolidButton
              onClick={handleWithdrawConfirm}
              disabled={
                !withdrawAmount ||
                isPending ||
                isConfirming ||
                stakedBalance < withdrawAmount ||
                !!lastDepositMessage
              }
            >
              {lastDepositMessage ||
                (stakedBalance < withdrawAmount
                  ? 'Insufficient balance'
                  : `Confirm`)}
              {/* ${
                      !withdrawAmount
                        ? ''
                        : `-${withdrawAmount.toLocaleString()} Voting Power`
                    } */}
            </SolidButton>
          )}
        </>
      )}
    </div>
  )
}
